/**
 * SeverityLegend — the ANA.4 legend (CLAUDE.md §3.5; style guide §2.5; mission
 * 6 WP03 T022; FR-023, C-003; research §1.10; post-tasks P4, E8, R14;
 * DM-01M3C8MGK4F8V0M89K52YPHJ39).
 *
 * The disclosure button holds the three severity badges (no invented label).
 * Each `<dt>` holds a badge and each `<dd>` the PRD definition from
 * `ANA4_LEGEND`, verbatim. The attribution and the Rule catalog link are always
 * rendered, collapsed or not; there is no dismiss control.
 *
 * The open state lives in `localStorage['trellis.legend']`, exactly
 * `collapsed` or `expanded` (WP11's golden step and the harness read this key
 * with no fallback). No stored value is the first visit, which is expanded;
 * each toggle stores the new state; every access is guarded.
 */
import { useId, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { Link } from 'react-router-dom'

import { cn } from '@/components/ui/cn'
import { ANA4_LEGEND } from '@/lib/mandatory-copy'
import { SEVERITY_ORDER } from '@/lib/severity'

import { SeverityBadge } from './SeverityBadge'

export const LEGEND_STORAGE_KEY = 'trellis.legend'

type Stored = 'collapsed' | 'expanded'

function readStored(): Stored | null {
  try {
    const value = window.localStorage.getItem(LEGEND_STORAGE_KEY)
    return value === 'collapsed' || value === 'expanded' ? value : null
  } catch {
    return null
  }
}

function store(value: Stored): void {
  try {
    window.localStorage.setItem(LEGEND_STORAGE_KEY, value)
  } catch {
    // Storage refused (private mode, quota): the legend still toggles.
  }
}

export function SeverityLegend({ className }: { className?: string }) {
  const [expanded, setExpanded] = useState(() => readStored() !== 'collapsed')
  const listId = useId()

  function toggle() {
    const next = !expanded
    setExpanded(next)
    store(next ? 'expanded' : 'collapsed')
  }

  return (
    <section className={cn('rounded-lg border border-navy-200 bg-white px-4 py-3', className)}>
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={listId}
        onClick={toggle}
        className="inline-flex items-center gap-1.5 rounded-sm focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]"
      >
        {SEVERITY_ORDER.map((severity) => (
          <SeverityBadge key={severity} severity={severity} />
        ))}
        <ChevronDown
          aria-hidden
          className={cn(
            'size-4 text-navy-600 transition-transform duration-[var(--duration-fast)]',
            expanded && 'rotate-180',
          )}
        />
      </button>
      <dl id={listId} hidden={!expanded} className="mt-3 grid grid-cols-[auto_1fr] items-baseline gap-x-3 gap-y-2">
        {SEVERITY_ORDER.map((severity) => (
          <div key={severity} className="contents">
            <dt>
              <SeverityBadge severity={severity} />
            </dt>
            <dd className="text-body text-navy-950">{ANA4_LEGEND[severity]}</dd>
          </div>
        ))}
      </dl>
      <p className="mt-3 text-body-sm text-navy-600">
        {ANA4_LEGEND.attribution}{' '}
        <Link to="/app/rules" className="text-[var(--color-link)] underline hover:text-[var(--color-link-hover)]">
          Rule catalog
        </Link>
      </p>
    </section>
  )
}
